import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { CaptchaField, type CaptchaFieldHandle } from '@/components/Auth/CaptchaField';
import { verifyCaptcha } from '@/lib/captcha';

// Pure helper extracted for unit testing. Calls Supabase Auth's
// `resetPasswordForEmail` with the correct `${origin}/reset-password` redirect
// and swallows Supabase-side errors (logging them) so the caller can keep the
// no-enumeration success UX. The promise still rejects on hard network /
// client failures so callers can surface a destructive toast.
export const requestPasswordReset = async (
  targetEmail: string,
  origin: string
): Promise<void> => {
  const { error: supabaseError } = await supabase.auth.resetPasswordForEmail(
    targetEmail,
    {
      redirectTo: `${origin}/reset-password`,
    }
  );

  if (supabaseError) {
    // Log for Sentry once that's wired up; do NOT show the user a "user not
    // found" style message even if Supabase ever surfaces one here.
    console.error('resetPasswordForEmail error', supabaseError);
  }
};

const ForgotPassword: React.FC = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [canResend, setCanResend] = useState(true);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef<CaptchaFieldHandle | null>(null);

  // Trigger the actual Supabase recovery email. Errors are swallowed for the
  // no-enumeration UX: the user always sees the same success state, regardless
  // of whether the email exists. We still surface a toast for transient client
  // errors (e.g. network down) so the user knows to retry. The helper is
  // exported as `requestPasswordReset` for unit testing.
  const requestReset = (targetEmail: string) =>
    requestPasswordReset(targetEmail, window.location.origin);

  // Start (or restart) the 30-second resend cooldown. Clearing any prior
  // interval prevents a memory leak if the user double-fires or navigates
  // away mid-countdown.
  const startResendCooldown = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    let timeLeft = 30;
    setCountdown(timeLeft);
    setCanResend(false);
    timerRef.current = setInterval(() => {
      timeLeft -= 1;
      setCountdown(timeLeft);
      if (timeLeft === 0) {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = null;
        setCanResend(true);
      }
    }, 1000);
  };

  // Ensure the interval is cleared on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Por favor ingresa tu correo electrónico');
      return;
    }

    if (!captchaToken) {
      setError('Por favor verifica que no eres un robot.');
      return;
    }

    setIsLoading(true);

    try {
      // Captcha must be server-verified before we hit Supabase Auth. If the
      // verification fails we reset the widget and bail — same no-enumeration
      // UX otherwise (the user still doesn't learn whether the email exists).
      const captchaResult = await verifyCaptcha(captchaToken, 'forgot_password');
      if (!captchaResult.success) {
        captchaRef.current?.reset();
        setCaptchaToken('');
        toast({
          variant: 'destructive',
          title: 'Captcha inválido',
          description: 'No pudimos verificar el captcha. Inténtalo de nuevo.',
        });
        setError('Captcha inválido. Inténtalo de nuevo.');
        setIsLoading(false);
        return;
      }

      await requestReset(email);

      setIsSubmitted(true);
      startResendCooldown();
    } catch (err) {
      // Network / unexpected client failure — keep the user on the form and
      // tell them to retry. This intentionally does NOT distinguish "email
      // exists" from "email doesn't exist".
      toast({
        variant: 'destructive',
        title: 'No pudimos enviar el correo',
        description:
          'Hubo un problema al procesar tu solicitud. Por favor intenta de nuevo en unos momentos.',
      });
      setError('Error al enviar el correo. Por favor intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || !email) return;
    setCanResend(false);
    try {
      await requestReset(email);
      toast({
        title: 'Correo reenviado',
        description: 'Si existe una cuenta, recibirás un nuevo enlace en unos minutos.',
      });
      startResendCooldown();
    } catch (err) {
      setCanResend(true);
      toast({
        variant: 'destructive',
        title: 'No pudimos reenviar el correo',
        description: 'Por favor intenta de nuevo en unos momentos.',
      });
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-blue-600" />
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Correo enviado
            </h2>

            <p className="text-gray-600 mb-6">
              Si existe una cuenta asociada a <strong>{email}</strong>,
              recibirás un enlace para restablecer tu contraseña en los próximos minutos.
            </p>

            <div className="space-y-4">
              <div className="text-sm text-gray-500">
                <p>¿No recibiste el correo?</p>
                <ul className="mt-2 space-y-1">
                  <li>• Revisa tu carpeta de spam</li>
                  <li>• Verifica que el correo sea correcto</li>
                  <li>• Espera unos minutos más</li>
                </ul>
              </div>

              <Button
                onClick={handleResend}
                disabled={!canResend}
                variant="outline"
                className="w-full"
              >
                {canResend ? 'Reenviar correo' : `Reenviar en ${countdown}s`}
              </Button>

              <Link to="/login">
                <Button variant="outline" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver a iniciar sesión
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <h1 className="text-3xl font-bold text-blue-600">CoPlanner</h1>
        </Link>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Recupera tu acceso
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            <div>
              <Label htmlFor="email">Correo electrónico</Label>
              <div className="relative mt-1">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  placeholder="tu@email.com"
                />
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>

            <CaptchaField
              ref={captchaRef}
              onVerify={(token) => setCaptchaToken(token)}
            />

            <div>
              <Button
                type="submit"
                disabled={isLoading || !captchaToken}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isLoading ? 'Enviando enlace...' : 'Enviar enlace de recuperación'}
              </Button>
            </div>

            <div className="text-center">
              <Link
                to="/login"
                className="flex items-center justify-center text-sm text-blue-600 hover:text-blue-500"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Volver a iniciar sesión
              </Link>
            </div>
          </form>

          <div className="mt-6 border-t border-gray-200 pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">
                ¿No tienes cuenta?{' '}
                <Link to="/signup" className="font-medium text-blue-600 hover:text-blue-500">
                  Regístrate gratis
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
